import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

const TRACK_URL = '/track1.mp3';

const MusicPlayer = ({ onPlayStateChange }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const audioRef = useRef(null);
  
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.loop = true;
    }
  }, [volume]);
  
  useEffect(() => {
    if (onPlayStateChange) {
      onPlayStateChange(isPlaying);
    }
  }, [isPlaying, onPlayStateChange]);
  
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(err => console.log('Playback error:', err));
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Delay collapse to allow for volume adjustment
    setTimeout(() => {
      setIsExpanded(false);
    }, 300);
  };
  
  return (
    <div 
      className={`spherical-player ${isHovered ? 'hovered' : ''} ${isPlaying ? 'playing' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <audio
        ref={audioRef}
        src={TRACK_URL}
        loop
        preload="auto"
      />
      
      {/* Main spherical button */}
      <button 
        className="sphere-btn"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        data-testid="music-play-button"
      >
        <div className="sphere-glow" />
        <div className="sphere-inner">
          {isPlaying ? <Pause size={18} /> : <Play size={18} />}
        </div>
        {isPlaying && <div className="sphere-pulse" />}
      </button>
      
      {/* Volume controls - appear on hover */}
      <div className={`sphere-volume ${isExpanded ? 'visible' : ''}`}>
        <button 
          className="sphere-mute-btn"
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted || volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
        
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolumeChange}
          className="sphere-volume-slider"
          aria-label="Volume"
        />
      </div>
    </div>
  );
};

export default MusicPlayer;
